

// ============================================================================
// utils.js - UPDATED VERSION
// ============================================================================
import { formatCurrency } from "../../../Utils/formatNumbers";

export const formatAmount = formatCurrency;

const getAppOrigin = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "https://www.useblitz.co";
};

const buildInvoiceUrl = (path) => `${getAppOrigin()}${path}`;

export const getPaymentName = (invoice, campaignNames) => {
  if (invoice.campaign_id) {
    return campaignNames[invoice.campaign_id] || "Unknown Campaign";
  } else if (invoice.campaign_name) {
    return invoice.campaign_name;
  }
  return "BlitzPay Payment";
};

export const getUtilizationColor = (percentage) => {
  if (percentage >= 90) return '#ff5722';
  if (percentage >= 75) return '#ff9800';
  if (percentage >= 50) return '#2196f3';
  return '#4caf50';
};

export const getInvoiceStatus = (invoice = {}) =>
  (invoice.invoice_status || invoice.status || "").toLowerCase();

export const isPendingInvoice = (invoice = {}) =>
  getInvoiceStatus(invoice) === "pending";

export const isBlitzPayOutgoingInvoice = (invoice = {}) =>
  invoice.invoice_type === "blitzpay" || invoice.source_type === "blitzpay";

export const getPendingOutgoingInvoices = (invoices = []) =>
  invoices.filter(
    (invoice) =>
      isPendingInvoice(invoice) && isBlitzPayOutgoingInvoice(invoice)
  );

export const getPendingIncomingInvoices = (incomingInvoices = []) =>
  incomingInvoices.filter((invoice) => isPendingInvoice(invoice));

export const buildPendingItems = ({
  pendingInvoices = [],
  incomingInvoices = [],
  getPaymentName,
}) =>
  [
    ...pendingInvoices.map((invoice) => ({
      ...invoice,
      id: invoice.id,
      name: getPaymentName
        ? getPaymentName(invoice)
        : (invoice.campaign_name || "Invoice"),
      amount: invoice.amount_due || invoice.amount,
      date: invoice.created_at,
      status: invoice.status,
      creatorId: invoice.creator_id,
      po_number: invoice.po_number,
      isIncoming: false,
      type: "outgoing",
    })),
    ...incomingInvoices.map((invoice) => ({
      ...invoice,
      id: invoice.id,
      name: invoice.client_name || "Incoming Invoice",
      amount: invoice.amount || invoice.amount_due,
      date: invoice.created_at || invoice.payout_date,
      status: invoice.invoice_status || invoice.status,
      creatorId: invoice.creator_id,
      po_number: invoice.po_number,
      isIncoming: true,
      type: "incoming",
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

/**
 * Calculate summary metrics
 * pendingItems = ALL pending invoices (outgoing + incoming)
 */
export const calculateSummaryMetrics = (invoices, payouts, incomingInvoices) => {
  const totalInvoices = invoices.length;
  
  const invoicesByStatus = invoices.reduce((acc, invoice) => {
    acc[invoice.status] = (acc[invoice.status] || 0) + 1;
    return acc;
  }, {});

  const creatorsPaid = payouts.length;
  
  const amountInEscrow = payouts.reduce((acc, curr) => {
    if (["pending", "queued"].includes((curr.status || "").toLowerCase())) {
      return acc + curr.amount;
    }
    return acc;
  }, 0);

  const paymentsThisMonth = payouts.reduce((acc, curr) => {
    const payoutDate = new Date(curr.payout_date);
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    if (
      payoutDate.getMonth() === currentMonth &&
      payoutDate.getFullYear() === currentYear
    ) {
      return acc + curr.amount;
    }
    return acc;
  }, 0);

  const pendingIncomingInvoices = getPendingIncomingInvoices(incomingInvoices);
  const pendingOutgoingInvoices = getPendingOutgoingInvoices(invoices);
  
  // Total pending items = outgoing + incoming
  const pendingItems =
    pendingOutgoingInvoices.length + pendingIncomingInvoices.length;

  return {
    totalInvoices,
    invoicesByStatus,
    creatorsPaid,
    amountInEscrow,
    paymentsThisMonth,
    totalIncomingAmount: pendingIncomingInvoices.length,
    pendingInvoices: pendingOutgoingInvoices,
    pendingIncomingInvoices,
    pendingItems
  };
};

/**
 * Prepare combined activity
 */
export const prepareCombinedActivity = (
  filteredInvoices,
  filteredIncomingInvoices,
  payouts,
  statusFilter,
  startDate,
  endDate,
  getPaymentName
) => {
  // Outgoing invoices
  const invoiceActivity = filteredInvoices
    .filter((inv) => inv.status !== "cancelled")
    .map((inv) => ({
      type: "Invoice",
      name: getPaymentName(inv),
      po_number: inv.po_number || "N/A",
      amount: inv.amount_due,
      status: inv.status,
      date: inv.created_at,
      notes: inv.notes,
      link: buildInvoiceUrl(`/invoicing/${inv.id}`),
      editable: inv.status !== "Paid",
      pending: inv.status === "pending",
      invoiceId: inv.id,
      isIncoming: false,
    }));

  // Incoming invoices
  const incomingInvoiceActivity = filteredIncomingInvoices.map((inv) => {
    const isClient = Object.prototype.hasOwnProperty.call(inv, "amount_due");
    const status = inv.invoice_status || inv.status;
    const isPending = status?.toLowerCase() === "pending";
    
    return {
      type: "Incoming Invoice",
      name: inv.client_name || "Unknown Client",
      po_number: inv.po_number || "N/A",
      amount: inv.amount,
      status: inv.invoice_status,
      date: inv.payout_date || inv.created_at,
      notes: inv.notes,
      link: isClient
        ? buildInvoiceUrl(`/invoicing/${inv.id}`)
        : buildInvoiceUrl(`/creatorinvoice/${inv.id}`),
      editable: false,
      pending: isPending,
      invoiceId: inv.id,
      isIncoming: true,
      isClient,
      creatorId: inv.creator_id,
    };
  });

  // Payouts
  const payoutActivity = payouts
    .filter((p) => {
      const dateCheck =
        new Date(p.payout_date) >= new Date(startDate) &&
        new Date(p.payout_date) <= new Date(endDate);
      const statusCheck = !statusFilter || p.status === statusFilter;
      return dateCheck && statusCheck;
    })
    .map((p) => ({
      type: "Payout",
      name: p.campaignname || "Creator Payout",
      po_number: "N/A",
      amount: p.amount,
      status: p.status,
      date: p.payout_date,
      notes: p.notes,
      link: "#",
      editable: false,
      pending: false,
      invoiceId: null,
      isIncoming: false,
      creatorId: p.creator_id,
    }));

  return [
    ...invoiceActivity,
    ...incomingInvoiceActivity,
    ...payoutActivity,
  ].sort((a, b) => new Date(b.date) - new Date(a.date));
};

export const getInvoiceLink = (invoice, needsApproval = null) => {
  if (invoice.type === "Payout" || !invoice.invoiceId) {
    return "#";
  }
  
  let baseUrl;
  if (invoice.isIncoming) {
    baseUrl = invoice.isClient
      ? buildInvoiceUrl(`/invoicing/${invoice.invoiceId}`)
      : buildInvoiceUrl(`/creatorinvoice/${invoice.invoiceId}`);
  } else {
    baseUrl = buildInvoiceUrl(`/invoicing/${invoice.invoiceId}`);
  }
  
  // Append approval query param for incoming pending invoices
  const shouldAppendApproval = invoice.isIncoming && invoice.pending;
  
  if (shouldAppendApproval) {
    return `${baseUrl}?needsApproval=true`;
  }
  
  return baseUrl;
};

/**
 * Get status color for chips
 */
export const getStatusColor = (status) => {
  const statusLower = status?.toLowerCase();
  
  if (statusLower === "paid" || statusLower === "completed") {
    return "success";
  }
  if (statusLower === "pending") {
    return "warning";
  }
  if (statusLower === "cancelled" || statusLower === "declined") {
    return "error";
  }
  
  return "default";
};
